// frontend/src/components/PreferenceTable.js

import React from 'react';
import { useDroppable, useDraggable } from '@dnd-kit/core';
import '../styles/PreferenceTable.css';

const PreferenceTable = ({
  preferences,
  isSidebarOpen,
  onRemove,
  onDrop,
  duplicates,
  departmentTermCapacities,
  fixedCycles,
}) => {
  const cycles = [
    '第1クール',
    '第2クール',
    '第3クール',
    '第4クール',
    '第5クール',
    '第6クール',
    '第7クール',
  ];
  const preferenceOrder = Array.from({ length: 20 }, (_, i) => `第${i + 1}希望`);

  const getCapacity = (value, cycle) => {
    if (!value || !value.department_term_capacity_id) return null;
    const termId = cycles.indexOf(cycle) + 1;
    const capacityInfo = departmentTermCapacities.find(
      (cap) => cap.id === value.department_term_capacity_id && cap.term_id === termId
    );
    return capacityInfo ? (capacityInfo.capacity ?? 0) : null;
  };

  return (
    <div className={`preference-table-container ${isSidebarOpen ? 'sidebar-open' : ''} overflow-x-auto`} data-tour="first-preference-table">
      <table className="w-full h-full border-collapse text-sm">
        <thead>
          <tr>
            <th className="w-20 p-2 border-b-2 border-gray-200 bg-gray-50 text-left font-semibold text-gray-600 sticky left-0 z-10"></th>
            {cycles.map((cycle, index) => (
              <th 
                key={cycle} 
                className={`min-w-[140px] p-2 border-b-2 border-gray-200 bg-gray-50 text-center font-semibold text-gray-600 ${fixedCycles.includes(cycle) ? 'bg-gray-200' : ''}`}
                data-tour={index === 0 ? 'first-cycle-header' : undefined}
              >
                <div className="flex flex-col items-center gap-2 py-2">
                  <span className="font-bold text-gray-700">{cycle}</span>
                  {fixedCycles.includes(cycle) && (
                    <span className="text-xs bg-gray-600 text-white px-2 py-0.5 rounded" data-tour="fixed-cycle-badge">固定</span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {preferenceOrder.map((preference, rowIndex) => (
            <tr key={preference} className="hover:bg-gray-50 transition-colors">
              <td className="p-3 border-b border-gray-200 font-medium text-gray-700 bg-white sticky left-0 z-10 shadow-[2px_0_5px_-2px_rgba(0,0,0,0.1)]">
                {preference}
              </td>
              {cycles.map((cycle, colIndex) => {
                const isCycleFixed = fixedCycles.includes(cycle);
                const value = preferences && preferences[preference] && preferences[preference][cycle];
                const isDuplicate =
                  value && duplicates[cycle] && duplicates[cycle][value.department_term_capacity_id];
                const capacity = getCapacity(value, cycle);
                const isInvalidCapacity = capacity !== null && capacity <= 0;
                const isInvalid = value && value.isInvalid;
                const isFixed = isCycleFixed;

                return (
                  <DroppableCell
                    key={`${preference}-${cycle}`}
                    id={`preference-${preference}-${cycle}`}
                    value={value}
                    onRemove={() => onRemove(preference, cycle)}
                    onDrop={(item) => onDrop(item, preference, cycle)}
                    isDuplicate={isDuplicate}
                    isInvalidCapacity={isInvalidCapacity}
                    isInvalid={isInvalid}
                    isFixed={isFixed}
                    data-department-id={value && value.department_term_capacity_id}
                    data-preference-order={parseInt(preference.replace('第', '').replace('希望', ''))}
                    data-tour={rowIndex === 0 && colIndex === 0 ? 'first-preference-cell' : undefined}
                  >
                    {value && (
                      <DraggableItem
                        id={`preference-${preference}-${cycle}`}
                        data={value}
                        isDuplicate={isDuplicate}
                        isInvalidCapacity={isInvalidCapacity}
                        isInvalid={isInvalid}
                        isFixed={isFixed}
                      >
                        {value.display_name}
                      </DraggableItem>
                    )}
                  </DroppableCell>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

const DroppableCell = ({
  id,
  children,
  onRemove,
  onDrop,
  isDuplicate,
  isInvalidCapacity,
  isInvalid,
  isFixed,
  ...props
}) => {
  const { setNodeRef, isOver } = useDroppable({
    id,
    disabled: isFixed,
  });

  const handleContextMenu = (e) => {
    if (isFixed) return;
    e.preventDefault();
    onRemove();
  };

  if (isFixed) {
    return (
      <td className="p-2 border-b border-gray-200 bg-gray-100 text-center align-middle" {...props}>
        <div className="h-full min-h-12 flex items-center justify-center text-gray-500 text-sm font-medium bg-gray-100 rounded border border-gray-300">
          {children || "固定"}
        </div>
      </td>
    );
  }

  let cellClass = "h-full flex items-center justify-center rounded border transition-all duration-200 min-h-12 p-1 text-base text-center break-words";
  
  if (isDuplicate) {
    cellClass += " bg-red-50 border-red-300 text-red-700";
  } else if (isInvalidCapacity) {
    cellClass += " bg-orange-50 border-orange-300 text-orange-700";
  } else if (isInvalid) {
    cellClass += " bg-yellow-50 border-yellow-300 text-yellow-700";
  } else if (children) {
    cellClass += " bg-[#1e40af] border-[#1e40af] text-white shadow-base";
  } else {
    cellClass += " bg-white border-gray-200 text-gray-400 hover:border-blue-300 hover:bg-blue-50/30";
  }

  if (isOver) {
    cellClass += " ring-2 ring-blue-400 ring-offset-1 bg-blue-100 border-blue-400";
  }

  return (
    <td
      ref={setNodeRef}
      className="p-2 border-b border-gray-200 align-middle"
      onContextMenu={handleContextMenu}
      {...props}
    >
      <div className={cellClass}>
        {children}
      </div>
    </td>
  );
};

const DraggableItem = ({
  id,
  data,
  children,
  isDuplicate,
  isInvalidCapacity,
  isInvalid,
  isFixed,
}) => {
  const { attributes, listeners, setNodeRef, transform } = useDraggable({
    id,
    data: { value: data },
    disabled: isFixed,
  });

  const style = transform
    ? {
        transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`,
        zIndex: 1000,
      }
    : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className="w-full h-full flex items-center justify-center cursor-grab active:cursor-grabbing"
    >
      {children}
    </div>
  );
};

export default PreferenceTable;
